import {
  ORDER_FLOW,
  ORDER_STATUS_LABELS,
  Order,
  OrderMessages,
  type OrderStatus,
  type OrderSummary,
  formatBRL,
  formatIsoDate,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Icon } from '../../components/Icon';
import { PageHead } from '../../components/PageHead';
import { EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';
import { useChannels, useKits } from '../../lib/catalog-api';
import { Sheet } from '../../components/Overlays';
import { errorMessage } from '../../lib/messages';
import {
  type OrderFilters,
  orderParams,
  useInvalidateOrders,
  useOrders,
} from '../../lib/orders-api';
import { useIsPhone } from '../../lib/use-media';
import { ChannelDot, ShipDialog, StatusBadge, copyToClipboard, shipText } from './common';
import { ExportCsvButton } from '../../components/ExportCsv';
import { Combobox } from '../../components/Combobox';
import { channelOption } from '../../components/pickers';

const LANES: OrderStatus[] = ['new', 'in_production', 'to_pack', 'shipped'];
const TABS: (OrderStatus | 'all')[] = ['all', ...ORDER_FLOW, 'cancelled', 'returned'];
const ACTION_FOR: Partial<Record<OrderStatus, { label: string; path: string }>> = {
  new: { label: 'Em produção', path: 'confirm' },
  in_production: { label: 'Para embalar', path: 'to-pack' },
  shipped: { label: 'Fechado', path: 'close' },
};

function useAdvance() {
  const toast = useToast();
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (v: { o: OrderSummary; path: string }) =>
      api.post(`/orders/${v.o.id}/${v.path}`, Order),
    onSuccess: (o) => {
      invalidate();
      toast(`${o.number} movido para ${ORDER_STATUS_LABELS[o.status]}`);
    },
    onError: (err) => toast(errorMessage(err)),
  });
}

function footer(o: OrderSummary): { left: string; right: string } {
  const ship = shipText(o);
  if (o.status === 'in_production') {
    return {
      left: `${o.production.produced} de ${o.production.total} prontos`,
      right: o.shipBy ? `prazo ${formatIsoDate(o.shipBy).slice(0, 5)}` : '',
    };
  }
  if (o.status === 'to_pack')
    return { left: `checklist ${o.checklistDone} de 4`, right: formatBRL(o.totalCentavos) };
  if (o.status === 'shipped')
    return {
      left: o.trackingCode ?? (o.shippingMethod === 'hand' ? 'entrega em mãos' : 'sem rastreio'),
      right: formatBRL(o.totalCentavos),
    };
  return {
    left: ship.text.replace('Prazo de envio', 'prazo de envio'),
    right: formatBRL(o.totalCentavos),
  };
}

function BoardCard({ o, onShip }: { o: OrderSummary; onShip: () => void }) {
  const advance = useAdvance();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(false);
  const next = ACTION_FOR[o.status];
  const f = footer(o);
  return (
    <div
      className="card col"
      style={{ gap: 6, padding: 12, border: o.late ? '2px solid var(--err)' : undefined }}
    >
      <div className="row between" style={{ gap: 6 }}>
        <Link to={`/pedidos/${o.id}`} style={{ fontWeight: 600 }}>
          {o.number}
        </Link>
        <span className="row" style={{ gap: 4 }}>
          <ChannelDot channel={o.channel} />
          <button
            type="button"
            className="btn sm g icon"
            aria-label={`Mover ${o.number}`}
            onClick={() => setMenu(!menu)}
          >
            ⋯
          </button>
        </span>
      </div>
      <span className="sm trunc">
        {o.itemsSummary}
        {o.customer ? ` · ${o.customer.name}` : ''}
      </span>
      {o.status === 'in_production' ? (
        <div className="layers thin">
          <div
            className="fill"
            style={{
              width: `${Math.round((o.production.produced / Math.max(1, o.production.total)) * 100)}%`,
            }}
          />
        </div>
      ) : null}
      {o.late && o.shipBy ? (
        <span className="xs" style={{ color: 'var(--err)' }}>
          Atrasado · prazo era {formatIsoDate(o.shipBy).slice(0, 5)}
        </span>
      ) : null}
      <div className="row between xs muted">
        <span>{f.left}</span>
        <span>{f.right}</span>
      </div>
      {o.fromStock && o.status === 'new' ? (
        <span className="badge ok" style={{ alignSelf: 'flex-start' }}>
          Tem em pronta entrega
        </span>
      ) : null}
      {menu ? (
        <div className="card sunk col" style={{ gap: 4, padding: 8, borderRadius: 10 }}>
          {o.status === 'to_pack' ? (
            <button type="button" className="btn sm" onClick={onShip}>
              Mover para Enviado
            </button>
          ) : next ? (
            <button
              type="button"
              className="btn sm"
              disabled={advance.isPending}
              onClick={() => {
                setMenu(false);
                advance.mutate({ o, path: next.path });
              }}
            >
              Mover para {next.label}
            </button>
          ) : null}
          <button
            type="button"
            className="btn sm g"
            onClick={() => void navigate(`/pedidos/${o.id}`)}
          >
            Abrir pedido
          </button>
        </div>
      ) : null}
    </div>
  );
}

const isClosing = (name: string) => /caixa|envelope/i.test(name);
const qty = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

/** "Embalar P-…" (Pedidos-Celular): a four-step check, then "Embalado" (the kit leaves stock). */
function PackSheet({ o, onClose }: { o: OrderSummary; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateOrders();
  const kits = useKits();
  const kit = kits.data?.data.find((k) => k.id === o.packagingKit?.id);
  const protection = (kit?.items ?? []).filter(
    (i) => !isClosing(i.name) && !/cart[aã]o/i.test(i.name),
  );
  const closing = (kit?.items ?? []).find((i) => isClosing(i.name));
  const rows = [
    { title: `${o.itemsSummary} conferido`, sub: 'sem fios, marcas de suporte ou rachaduras' },
    {
      title: protection.length
        ? protection.map((i) => `${i.name} ${qty(i.quantity)} ${i.unit}`).join(' + ')
        : 'Peças protegidas',
      sub: 'nada solto dentro da embalagem',
    },
    { title: 'Cartão de agradecimento', sub: 'com o QR do Instagram' },
    {
      title: closing ? `${closing.name} fechada` : 'Embalagem fechada',
      sub: o.packagingKit?.name ?? 'kit de embalagem',
    },
  ];
  const [done, setDone] = useState<boolean[]>(() => rows.map(() => false));
  const count = done.filter(Boolean).length;
  const pack = useMutation({
    mutationFn: () => api.post(`/orders/${o.id}/checklist/packed`, Order, { done: true }),
    onSuccess: () => {
      invalidate();
      toast(`${o.number} embalado. Falta marcar como enviado.`);
      onClose();
    },
    onError: (err) => toast(errorMessage(err)),
  });
  return (
    <Sheet label={`Embalar ${o.number}`} onClose={onClose}>
      <div>
        <h2>Embalar {o.number}</h2>
        <p className="sm muted">
          {o.itemsSummary}
          {o.packagingKit ? ` · ${o.packagingKit.name}` : ''}
        </p>
      </div>
      {rows.map((r, i) => (
        <button
          key={r.title}
          type="button"
          className="check sm"
          aria-pressed={done[i]}
          onClick={() => setDone(done.map((d, k) => (k === i ? !d : d)))}
        >
          <span className={done[i] ? 'box on' : 'box'}>{done[i] ? '✓' : null}</span>
          <span className="grow" style={{ textAlign: 'left' }}>
            {r.title}
            <span className="xs muted" style={{ display: 'block' }}>
              {r.sub}
            </span>
          </span>
        </button>
      ))}
      <p className="sm muted">
        {count === rows.length
          ? 'Tudo conferido. O kit sai do estoque de insumos.'
          : `${count} de ${rows.length} conferidos`}
      </p>
      <button
        type="button"
        className={count === rows.length ? 'btn p block' : 'btn block'}
        disabled={pack.isPending}
        onClick={() =>
          count === rows.length
            ? pack.mutate()
            : toast('Confira os 4 itens antes de fechar a embalagem.')
        }
      >
        Marcar embalado
      </button>
    </Sheet>
  );
}

function PhoneCard({ o, onShip }: { o: OrderSummary; onShip: () => void }) {
  const advance = useAdvance();
  const toast = useToast();
  const ship = shipText(o);
  const [packing, setPacking] = useState(false);
  return (
    <div
      className="card col"
      style={{ gap: 6, border: o.late ? '2px solid var(--err)' : undefined }}
    >
      <Link to={`/pedidos/${o.id}`} className="col" style={{ gap: 6 }}>
        <div className="row between" style={{ gap: 6 }}>
          <span className="row" style={{ gap: 8 }}>
            <b>{o.number}</b>
            <ChannelDot channel={o.channel} />
          </span>
          <StatusBadge status={o.status} />
        </div>
        <span className="sm">
          {o.itemsSummary}
          {o.customer ? ` · ${o.customer.name}` : ''}
          {o.marketplaceOrderNo ? ` · nº ${o.marketplaceOrderNo}` : ''}
        </span>
        <span className="row between sm">
          <span
            style={{
              color:
                ship.tone === 'err'
                  ? 'var(--err)'
                  : ship.tone === 'warn'
                    ? 'var(--warn)'
                    : 'var(--ink2)',
            }}
          >
            {ship.text}
          </span>
          <b className="tab">{formatBRL(o.totalCentavos)}</b>
        </span>
        {o.status === 'in_production' ? (
          <span className="xs muted">
            {o.production.produced} de {o.production.total} prontos · {o.production.queued} na fila
          </span>
        ) : null}
      </Link>
      <div className="row between" style={{ gap: 8 }}>
        <span className="xs muted">
          {o.status === 'to_pack'
            ? `checklist ${o.checklistDone} de 4`
            : o.marketplaceOrderNo
              ? `nº ${o.marketplaceOrderNo}`
              : 'venda direta'}
        </span>
        {o.status === 'new' ? (
          <button
            type="button"
            className="btn sm p"
            disabled={advance.isPending}
            onClick={() => advance.mutate({ o, path: 'confirm' })}
          >
            {o.fromStock ? 'Usar pronta entrega' : 'Pôr na fila'}
          </button>
        ) : o.status === 'in_production' ? (
          <Link className="btn sm g" to="/fila">
            Ver na fila
          </Link>
        ) : o.status === 'to_pack' && !o.packed ? (
          <button type="button" className="btn sm p" onClick={() => setPacking(true)}>
            Embalar
          </button>
        ) : o.status === 'to_pack' ? (
          <button type="button" className="btn sm p" onClick={onShip}>
            Marcar enviado
          </button>
        ) : o.status === 'shipped' ? (
          <button
            type="button"
            className="btn sm g"
            onClick={() =>
              void api
                .get(`/orders/${o.id}/messages`, OrderMessages)
                .then((m) => copyToClipboard(m.shipped))
                .then((ok) => toast(ok ? 'Copiado. É só colar.' : 'Não deu para copiar.'))
            }
          >
            Copiar “enviado”
          </button>
        ) : null}
      </div>
      {packing ? <PackSheet o={o} onClose={() => setPacking(false)} /> : null}
    </div>
  );
}

/** Pedidos (boards Pedidos-Quadro, Pedidos-Lista, Pedidos-Celular). */
export function OrdersPage() {
  const phone = useIsPhone();
  const channels = useChannels();
  const [view, setView] = useState<'board' | 'list'>('board');
  const [tab, setTab] = useState<OrderStatus | 'all' | 'late'>('all');
  // Calibração › "Ver pedidos" opens /pedidos?canal=<id>.
  const [initialChannel] = useState(
    () => new URLSearchParams(window.location.search).get('canal') ?? '',
  );
  const [f, setF] = useState<OrderFilters>({
    statuses: [],
    channelId: initialChannel,
    late: false,
    q: '',
    sort: 'ship_by',
  });
  const [shipping, setShipping] = useState<OrderSummary | null>(null);
  const [asc, setAsc] = useState(true);
  const list = useOrders({
    ...f,
    statuses: tab === 'all' || tab === 'late' ? [] : [tab],
    late: f.late || tab === 'late',
  });
  const all = useOrders({ ...f, statuses: [], late: false });
  const rows = list.data?.data ?? [];
  const counts = all.data?.counts ?? {};
  const allRows = all.data?.data ?? [];
  const header = (
    <PageHead
      crumb="Vender"
      title="Pedidos"
      actions={
        <div className="row wrap" style={{ gap: 10 }}>
          {phone ? null : <ExportCsvButton dataset="pedidos" params={orderParams(f)} />}
          {phone ? null : (
            <>
              <div className="seg" role="radiogroup" aria-label="Visualização">
                <button
                  type="button"
                  role="radio"
                  aria-checked={view === 'board'}
                  className={view === 'board' ? 'on' : ''}
                  onClick={() => setView('board')}
                >
                  Quadro
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={view === 'list'}
                  className={view === 'list' ? 'on' : ''}
                  onClick={() => setView('list')}
                >
                  Lista
                </button>
              </div>
              <Combobox
                ariaLabel="Canal"
                style={{ width: 200 }}
                value={f.channelId}
                options={[
                  { value: '', label: 'Todos os canais', pinned: 'top' },
                  ...(channels.data?.data ?? []).map(channelOption),
                ]}
                onChange={(v) => setF({ ...f, channelId: v })}
              />
              <button
                type="button"
                className="check sm"
                role="switch"
                aria-checked={f.late}
                onClick={() => setF({ ...f, late: !f.late })}
              >
                <span className={f.late ? 'toggle on' : 'toggle'} aria-hidden="true" />
                Só atrasados
              </button>
            </>
          )}
          <Link className="btn p" to="/pedidos/novo">
            <Icon name="plus" size={18} />
            Novo pedido
          </Link>
        </div>
      }
    />
  );
  const shipDialog = shipping ? (
    <ShipDialog order={shipping} onClose={() => setShipping(null)} />
  ) : null;

  if (all.isPending)
    return (
      <>
        {header}
        <Skeleton rows={3} height={90} />
      </>
    );
  if (all.isError) {
    return (
      <>
        {header}
        <ErrorBox
          title="Não foi possível carregar os pedidos"
          detail="Sem conexão com o servidor. Os pedidos continuam salvos."
          onRetry={() => void all.refetch()}
        />
      </>
    );
  }
  if (allRows.length === 0 && !f.channelId && !f.q) {
    return (
      <>
        {header}
        <div className="card">
          <EmptyState
            title="Nenhum pedido ainda"
            action={
              <Link className="btn p" to="/pedidos/novo">
                Registrar primeiro pedido
              </Link>
            }
          >
            Registre uma venda do Mercado Livre, Shopee ou direta para acompanhar produção, envio e
            lucro real.
          </EmptyState>
        </div>
      </>
    );
  }

  if (phone) {
    const open =
      tab === 'all'
        ? rows.filter((o) => !['closed', 'cancelled', 'returned'].includes(o.status))
        : rows;
    const ended = allRows.filter((o) =>
      ['closed', 'cancelled', 'returned'].includes(o.status),
    ).length;
    return (
      <div className="col" style={{ gap: 12 }}>
        {header}
        <p className="sm muted">
          {(counts.new ?? 0) + (counts.in_production ?? 0) + (counts.to_pack ?? 0)} em aberto ·{' '}
          {counts.late ?? 0} atrasados
        </p>
        <label className="search" style={{ maxWidth: 'none', flex: '0 0 auto', height: 48 }}>
          <Icon name="search" size={16} />
          <input
            aria-label="Buscar pedido"
            placeholder="Nº, cliente ou nº do marketplace"
            value={f.q}
            onChange={(e) => setF({ ...f, q: e.target.value })}
          />
        </label>
        <div className="row wrap" style={{ gap: 6 }}>
          {(['all', 'new', 'in_production', 'to_pack', 'shipped', 'late'] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={tab === t ? 'chip on' : 'chip'}
              style={t === 'late' ? { color: tab === t ? undefined : 'var(--err)' } : undefined}
              onClick={() => setTab(t)}
            >
              {t === 'all' ? 'Todos' : t === 'late' ? 'Atrasados' : ORDER_STATUS_LABELS[t]} ·{' '}
              {t === 'all' ? allRows.length - ended : (counts[t] ?? 0)}
            </button>
          ))}
        </div>
        {open.map((o) => (
          <PhoneCard key={o.id} o={o} onShip={() => setShipping(o)} />
        ))}
        {open.length === 0 ? (
          <div className="card">
            <EmptyState
              title="Nenhum pedido com esses filtros"
              action={
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setTab('all');
                    setF({ ...f, channelId: '', late: false, q: '' });
                  }}
                >
                  Limpar filtros
                </button>
              }
            >
              Troque a situação ou busque por outro nº ou cliente.
            </EmptyState>
          </div>
        ) : null}
        {ended ? (
          <p className="xs muted">{ended} fechados, cancelados ou devolvidos · ver no computador</p>
        ) : null}
        {shipDialog}
      </div>
    );
  }

  if (view === 'list') {
    const sorted = asc ? rows : rows.toReversed();
    const cols = '120px 2fr 1fr 130px 150px 110px';
    return (
      <>
        {header}
        <div className="tabs" role="tablist" aria-label="Situação">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={tab === t ? 'on' : ''}
              onClick={() => setTab(t)}
            >
              {t === 'all' ? 'Todos' : ORDER_STATUS_LABELS[t]} ·{' '}
              {t === 'all' ? allRows.length : (counts[t] ?? 0)}
            </button>
          ))}
        </div>
        <section className="card flat">
          <div className="gt" style={{ overflowX: 'auto' }}>
            <div className="gtr head" style={{ gridTemplateColumns: cols, minWidth: 760 }}>
              <span>Nº</span>
              <span>Itens · cliente</span>
              <span>Canal</span>
              <span>Situação</span>
              <button
                type="button"
                className="btn g sm"
                style={{ padding: 0, justifyContent: 'flex-start' }}
                aria-label={asc ? 'mais cedo primeiro' : 'mais tarde primeiro'}
                onClick={() => setAsc(!asc)}
              >
                Prazo de envio {asc ? '↑' : '↓'}
              </button>
              <span className="num">Total</span>
            </div>
            {sorted.map((o) => (
              <Link
                key={o.id}
                to={`/pedidos/${o.id}`}
                className={o.late ? 'gtr late' : 'gtr'}
                style={{ gridTemplateColumns: cols, minWidth: 760 }}
              >
                <b>{o.number}</b>
                <span className="trunc">
                  {o.itemsSummary}
                  {o.customer ? ` · ${o.customer.name}` : ''}
                </span>
                <ChannelDot channel={o.channel} />
                <span>
                  <StatusBadge status={o.status} />
                </span>
                <span className="sm" style={{ color: o.late ? 'var(--err)' : undefined }}>
                  {o.shipBy ? formatIsoDate(o.shipBy) : '—'}
                  {o.late ? ' · atrasado' : ''}
                </span>
                <span className="num">{formatBRL(o.totalCentavos)}</span>
              </Link>
            ))}
            {sorted.length === 0 ? (
              <div className="empty" style={{ padding: 24 }}>
                <p>Nenhum pedido com esses filtros.</p>
                <p className="sm muted">Troque o status, o canal ou desmarque “Só atrasados”.</p>
                <button
                  type="button"
                  className="btn sm"
                  onClick={() => {
                    setTab('all');
                    setF({ ...f, channelId: '', late: false, q: '' });
                  }}
                >
                  Limpar filtros
                </button>
              </div>
            ) : null}
          </div>
          <p className="xs muted" style={{ padding: '10px 18px' }}>
            {sorted.length} {sorted.length === 1 ? 'pedido' : 'pedidos'} · ordenado por prazo de
            envio
          </p>
        </section>
      </>
    );
  }

  const board = (all.data?.data ?? []).filter((o) => !f.late || o.late);
  const ended = board.filter((o) => o.status === 'cancelled' || o.status === 'returned');
  const filtered = !!(f.channelId || f.late || f.q.trim());
  const boardEmpty = all.data && LANES.every((s) => !board.some((o) => o.status === s));
  return (
    <>
      {header}
      {boardEmpty && filtered ? (
        <div className="card" style={{ marginBottom: 14 }}>
          <EmptyState
            title="Nenhum pedido aberto com esses filtros"
            action={
              <button
                type="button"
                className="btn"
                onClick={() => setF({ ...f, channelId: '', late: false, q: '' })}
              >
                Limpar filtros
              </button>
            }
          >
            Troque o canal ou desmarque “Só atrasados”.
          </EmptyState>
        </div>
      ) : null}
      <div
        style={{
          display: 'grid',
          gap: 14,
          gridTemplateColumns: 'repeat(4, minmax(220px, 1fr))',
          overflowX: 'auto',
          alignItems: 'start',
        }}
      >
        {LANES.map((s) => {
          const own = board.filter((o) => o.status === s);
          return (
            <section key={s} className="col" style={{ gap: 10, minWidth: 0 }}>
              <h2 style={{ fontSize: 16 }}>
                {ORDER_STATUS_LABELS[s]} <span className="muted tab">{own.length}</span>
              </h2>
              {own.map((o) => (
                <BoardCard key={o.id} o={o} onShip={() => setShipping(o)} />
              ))}
              {own.length === 0 ? <p className="sm muted">Nada aqui.</p> : null}
            </section>
          );
        })}
      </div>
      {ended.length || counts.closed ? (
        <section className="card col" style={{ gap: 6, marginTop: 22 }}>
          <div className="row between">
            <h2 style={{ fontSize: 18 }}>Cancelados e devolvidos</h2>
            <button
              type="button"
              className="btn sm g"
              onClick={() => {
                setView('list');
                setTab('closed');
              }}
            >
              Ver fechados ({counts.closed ?? 0})
            </button>
          </div>
          {ended.map((o) => (
            <Link key={o.id} to={`/pedidos/${o.id}`} className="row between" style={{ gap: 10 }}>
              <span className="row sm" style={{ gap: 8 }}>
                <b>{o.number}</b>
                <StatusBadge status={o.status} />
                <span className="muted trunc">
                  {o.channel.name} · {o.itemsSummary}
                </span>
              </span>
              <span
                className="sm"
                style={{ color: o.ended?.lossCentavos ? 'var(--err)' : 'var(--ok)' }}
              >
                {o.ended?.lossCentavos ? `perda ${formatBRL(o.ended.lossCentavos)}` : 'sem perda'}
              </span>
            </Link>
          ))}
        </section>
      ) : null}
      {shipDialog}
    </>
  );
}
